import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import NoResults from "../noResults/noResults";
import { useTranslator } from "../../context/translator";
import Can from "../can/Can";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { getRolesMaintenanceService } from "../../services/roles/services";
import RolePermissionsModal from "components/permissions/rolePermission/RolePermissionsModal";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";
import PermissionsConfigurationModal from "components/permissions/permissionsConfigurationModal/PermissionsConfigurationModal";

const ITEMS_PER_PAGE = 5;

const TableTeamRoles = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();

  const [rolesData, setRolesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRoles, setTotalRoles] = useState(10);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [rolePermissionsModalOpen, setRolePermissionsModalOpen] =
    useState(false);
  const [selectedRole, setSelectedRole] = useState({});

  const ModalRoleSPermissionshow = (rolee) => {
    setSelectedRole({ ...rolee });
    setRolePermissionsModalOpen(true);
  };
  const HandleRolePermissionsModalClose = (cerrado) => {
    setRolePermissionsModalOpen(false);
    setSelectedRole({});
  };

  const totalPages = Math.ceil(totalRoles / ITEMS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleNewTeamMember = () => navigate(`/team/roles/new`);

  const fetchTeamMembers = useCallback(async () => {
    try {
      setIsLoading(true);

      let params = {
        companyId: company?._id,
        page: page,
        search: search,
        pageSize: ITEMS_PER_PAGE,
      };

      const data = await getRolesMaintenanceService(params);

      setRolesData(data?.roles ?? []);
      setTotalRoles(data?.totalRoles ?? 0);
    } finally {
      setIsLoading(false);
    }
  }, [company?._id, page, search]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  if (userLoading || companyLoading) return null;

  return (
    <>
      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <div className="grid gap-6 mb-6 md:grid-cols-2 grid-cols-2 mt-2 mx-2">
          <div>
            <label
              htmlFor="search"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              {translate("search")}
            </label>
            <input
              id="search"
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder={`${translate("search")}...`}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                           focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 
                           dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <div className="flex justify-end items-center gap-4">
            <Can permissions={[PERMISSION_CODE.CREATE_ROLE]}>
              <button
                type="button"
                onClick={handleNewTeamMember}
                className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 
                           hover:bg-gradient-to-br focus:ring-4 focus:outline-none 
                           focus:ring-green-300 dark:focus:ring-green-800 font-medium rounded-lg 
                           text-sm px-5 py-2.5"
              >
                {translate("new-role")}
              </button>
            </Can>
          </div>
        </div>

        {isLoading && rolesData.length === 0 ? (
          <Loading />
        ) : rolesData.length === 0 ? (
          <NoResults message={translate("no-roles-found")} />
        ) : (
          <>
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">{translate("name")}</th>
                  <th className="px-6 py-3">{translate("description")}</th>
                  <th className="px-6 py-3">
                    <span className="sr-only">{translate("view")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rolesData?.map((emp) => (
                  <tr
                    key={emp._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 
                                 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {emp.name}
                    </th>
                    <td className="px-6 py-4">{emp.description}</td>

                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/team/roles/${emp._id}`}
                        className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                      >
                        {translate("view")}
                      </Link>

                      {(userProfile?.user?.role?.sa === true ||
                        userProfile?.user?.role?.admin === true) && (
                        <span>
                          &nbsp;&nbsp;
                          <button
                            type="button"
                            onClick={() => ModalRoleSPermissionshow(emp)}
                            className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                          >
                            {translate("permissions")}
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isLoading && <Loading />}

            <Pagination
              key="paginationTeamRoles"
              data={rolesData}
              page={page}
              pageNumber={totalPages}
              isLoading={isLoading}
              setPage={setPage}
              totalRecords={totalRoles}
            />

            <PermissionsConfigurationModal
              openP={rolePermissionsModalOpen}
              companyId={company.id}
              roleId={selectedRole._id}
              selectedType="ROLE"
              selectedItem={selectedRole}
              disabledPermissions={false}
              onClose={HandleRolePermissionsModalClose}
            />
          </>
        )}
      </div>

      <RolePermissionsModal
        openP={rolePermissionsModalOpen}
        selectedRole={selectedRole}
        onClose={HandleRolePermissionsModalClose}
      />
    </>
  );
};

export default TableTeamRoles;
